/*
 * Write-Flow-State pro Leaf. Ein Schreib-Vorgang laeuft in zwei diskreten
 * Stages die der Feld-Client bedient:
 *   1. Command → Server → MQTT → Driver.WriteAsync → Ack.
 *   2. Direkt danach fuehrt der Driver einen Immediate-Readback (IR) auf das
 *      gerade beschriebene Register aus. Das Ergebnis reist als `v` im
 *      Ack-Payload mit und laesst uns hier auf `confirmed` oder `mismatched`
 *      kippen ohne auf den naechsten regulaeren Poll-Zyklus zu warten.
 *      Kommt kein IR-Wert an (Setpoint-Rampe / Priority-Array / write-only
 *      Register), bleiben wir in `acked` und warten auf einen Poll-Value
 *      der matched.
 *
 * Kein fixer Readback-Timeout mehr: bei vielen DPs pro Poll-Zyklus ist die
 * Dauer bis der regulaere Value ankommt unvorhersehbar, ein fester Timeout
 * produziert zwangslaeufig falsche Warnungen (User-Feedback 2026-08-28).
 * Statt Timeout: `manualClear()` erlaubt dem Operator die Row jederzeit
 * wegzuraeumen falls der Regler wirklich nichts meldet.
 *
 * Comparator-Logik: exakter Match fuer Bool/Enum/Int/String, ±0.5 % relative
 * Toleranz fuer Float (Setpoints kommen manchmal minimal gerundet zurueck).
 */

import { signal, type Signal } from '@preact/signals';
import { showToast } from '../components/Toaster';

export type WriteStage = 'sending' | 'acked' | 'confirmed' | 'mismatched' | 'failed';

export interface WriteState {
  correlationId: string;
  leafId: string;
  sentValue: unknown;
  sentAt: number;
  ackedAt?: number;
  confirmedAt?: number;
  stage: WriteStage;
  errorMessage?: string;
  /** Vom Feld-Client zurueckgelesener Wert (Immediate-Readback oder erster
   *  matcher/nicht-matcher Poll-Value nach Ack). Fuer die "Regler haelt
   *  weiterhin X"-Anzeige bei stage === 'mismatched'. */
  actualValue?: unknown;
}

const bag = new Map<string, Signal<WriteState | null>>();
const timers = new Map<string, number>();

/** Nach einem Erfolg oder Fehler-Endzustand blenden wir die Row-Dekoration
 *  nach kurzer Zeit wieder aus. Mismatch bleibt laenger stehen weil der
 *  Operator die Erklaerung ("Regler nutzt X statt Y") lesen muss. */
const CLEAR_AFTER_MS_OK = 3_500;
const CLEAR_AFTER_MS_FAIL = 8_000;

/**
 * Reactive per-leaf Signal. Erstmalig angefordert → wird angelegt, danach
 * derselbe Signal-Instance zurueckgegeben, damit useSignal / <span>signal.value
 * konsistent subscribed. Signal.value === null wenn gerade nichts laeuft.
 */
export function writeSignalFor(leafId: string): Signal<WriteState | null> {
  let sig = bag.get(leafId);
  if (!sig) {
    sig = signal<WriteState | null>(null);
    bag.set(leafId, sig);
  }
  return sig;
}

/** SendCommandModal ruft das nach dem 202 auf. Setzt den Store auf "sending".
 *  Kein Hard-Timeout mehr — falls der Server nie antwortet bleibt die Row
 *  visuell in "sending" bis der Operator sie manuell wegraeumt oder eine
 *  Value-Update sie einholt. */
export function registerWrite(input: {
  leafId: string;
  correlationId: string;
  sentValue: unknown;
}): void {
  const sig = writeSignalFor(input.leafId);
  sig.value = {
    correlationId: input.correlationId,
    leafId: input.leafId,
    sentValue: input.sentValue,
    sentAt: Date.now(),
    stage: 'sending',
  };
  clearTimerFor(input.leafId);
}

/** Server-SignalR-Handler ruft das fuer jedes CommandEvent (Ack/Executed/
 *  Failed/Timeout) auf. Wenn das Event einen Immediate-Readback `v` traegt
 *  entscheiden wir direkt confirmed vs. mismatched; sonst bleiben wir in
 *  `acked` und warten auf den regulaeren Poll-Value via `reportValue`. */
export function onCommandEvent(evt: {
  correlationId: string;
  leafId: string;
  status: string;
  error?: string;
  readbackValue?: unknown;
}): void {
  const sig = bag.get(evt.leafId);
  if (!sig) return;
  const cur = sig.value;
  if (!cur || cur.correlationId !== evt.correlationId) return;

  const s = evt.status.toLowerCase();
  if (s === 'ack' || s === 'executed') {
    // Immediate-Readback vom Driver: sofort matchen und Endzustand setzen.
    if (evt.readbackValue !== undefined && evt.readbackValue !== null) {
      if (valuesMatch(cur.sentValue, evt.readbackValue)) {
        sig.value = {
          ...cur,
          stage: 'confirmed',
          ackedAt: Date.now(),
          confirmedAt: Date.now(),
          actualValue: evt.readbackValue,
        };
        scheduleClear(evt.leafId, evt.correlationId, CLEAR_AFTER_MS_OK);
        return;
      }
      // Regler hat das Kommando akzeptiert, aber der Register-Readback zeigt
      // einen anderen Wert — Priority-Array, Rampe, oder Regler-Override.
      // Endzustand rot, laenger stehen lassen damit der Operator die
      // Erklaerung liest.
      sig.value = {
        ...cur,
        stage: 'mismatched',
        ackedAt: Date.now(),
        actualValue: evt.readbackValue,
      };
      showToast({
        tone: 'warn',
        title: 'Wert nicht uebernommen',
        message: `Regler meldet ${formatValue(evt.readbackValue)} statt ${formatValue(cur.sentValue)}.`,
        autoHideAfterMs: 8000,
      });
      scheduleClear(evt.leafId, evt.correlationId, CLEAR_AFTER_MS_FAIL);
      return;
    }
    // Kein IR-Wert vorhanden (Driver konnte nicht lesen, oder Setpoint-
    // Rampe braucht Zyklen). In `acked` bleiben; `reportValue` schaltet
    // dann bei einem matchenden Poll-Value auf `confirmed`.
    if (cur.stage === 'sending') {
      sig.value = { ...cur, stage: 'acked', ackedAt: Date.now() };
    }
  } else if (s === 'failed' || s === 'timeout') {
    sig.value = { ...cur, stage: 'failed', errorMessage: evt.error };
    showToast({
      tone: 'error',
      title: s === 'timeout' ? 'Kommando-Timeout' : 'Kommando fehlgeschlagen',
      message: evt.error ?? undefined,
      autoHideAfterMs: 6000,
    });
    scheduleClear(evt.leafId, evt.correlationId, CLEAR_AFTER_MS_FAIL);
  }
}

/** ValueSignal-Watcher (LeafValueDisplay ruft das bei jeder neuen Value)
 *  vergleicht den frischen Value mit sentValue. Nur relevant wenn wir noch
 *  in `acked` sitzen (Driver hatte keinen Immediate-Readback zu liefern).
 *  Match → `confirmed`; ansonsten leise ignoriert, damit ein noch-nicht-
 *  gesetzter Wert nicht sofort als Mismatch gilt (der Regler braucht
 *  vielleicht ein paar Ticks bis die Rampe durch ist). */
export function reportValue(leafId: string, freshValue: unknown): void {
  const sig = bag.get(leafId);
  if (!sig) return;
  const cur = sig.value;
  if (!cur) return;
  if (cur.stage !== 'acked') return;
  if (!valuesMatch(cur.sentValue, freshValue)) return;
  sig.value = {
    ...cur,
    stage: 'confirmed',
    confirmedAt: Date.now(),
    actualValue: freshValue,
  };
  scheduleClear(leafId, cur.correlationId, CLEAR_AFTER_MS_OK);
}

/** Notausgang: der Operator raeumt die Write-Row manuell weg. Nutzt der UI-
 *  Layer per Klick am Toast-X falls der Regler wirklich nichts sagt (kein
 *  Poll-Value ankommt und auch kein IR-Wert im Ack war). */
export function manualClear(leafId: string): void {
  const sig = bag.get(leafId);
  if (!sig) return;
  clearTimerFor(leafId);
  sig.value = null;
}

function valuesMatch(sent: unknown, fresh: unknown): boolean {
  if (sent === null || fresh === null) return sent === fresh;
  if (typeof sent === 'boolean' || typeof fresh === 'boolean') return Boolean(sent) === Boolean(fresh);
  if (typeof sent === 'number' && typeof fresh === 'number') {
    if (!Number.isFinite(sent) || !Number.isFinite(fresh)) return false;
    if (Number.isInteger(sent) && Number.isInteger(fresh)) return sent === fresh;
    const scale = Math.max(1, Math.abs(sent));
    return Math.abs(sent - fresh) <= scale * 0.005;
  }
  if (typeof sent === 'string' && typeof fresh === 'string') return sent === fresh;
  // Mixed types (enum sent as label 'Water', readback as number 1): try
  // coerce both to string as a last resort.
  return String(sent) === String(fresh);
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'boolean') return v ? 'on' : 'off';
  return String(v);
}

function scheduleClear(leafId: string, correlationId: string, delayMs: number): void {
  clearTimerFor(leafId);
  timers.set(leafId, window.setTimeout(() => {
    const sig = bag.get(leafId);
    if (!sig) return;
    if (sig.value?.correlationId !== correlationId) return;
    sig.value = null;
    timers.delete(leafId);
  }, delayMs));
}

function clearTimerFor(leafId: string): void {
  const t = timers.get(leafId);
  if (t !== undefined) {
    window.clearTimeout(t);
    timers.delete(leafId);
  }
}
